import React, { createContext, useContext, useState, useEffect } from 'react';
import translations from './translations';

const LanguageContext = createContext();

const STORAGE_KEY = 'qd_language';
const DEFAULT_LANGUAGE = 'lt';
const SUPPORTED_LANGUAGES = ['lt', 'en'];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Try to get from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }
    
    // Try to detect from browser
    const browserLang = navigator.language?.slice(0, 2);
    if (SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }
    
    return DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
    }
  };

  // Translation function
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to default language
        value = translations[DEFAULT_LANGUAGE];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            console.warn(`Translation missing: ${key}`);
            return key;
          }
        }
        break;
      }
    }
    
    if (typeof value !== 'string') {
      console.warn(`Translation not a string: ${key}`);
      return key;
    }
    
    // Replace parameters
    let result = value;
    for (const [param, paramValue] of Object.entries(params)) {
      result = result.replace(new RegExp(`\\{${param}\\}`, 'g'), paramValue);
    }
    
    return result;
  };

  const value = {
    language,
    setLanguage,
    t,
    languages: SUPPORTED_LANGUAGES,
    languageNames: {
      lt: 'Lietuvių',
      en: 'English',
    },
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
